import { useMemo } from "react";

import { DIVIDER_OPTION_VALUE } from "@skilitics/designer-ui-components";

import type { DesignerNode } from "../../types/index.ts";
import { baseStates, draggableStates, droppableStates, hotZoneStates, useElementStateLabels } from "./shared.tsx";
import type { ElementState, ElementStateOption } from "./types.ts";

type Options = {
  hasDraggable?: boolean;
  hasDroppable?: boolean;
  hasHotZone?: boolean;
  hasDial?: boolean;
  hasSlider?: boolean;
  contentNode?: DesignerNode | null;
};

/**
 * Returns the list of element states that should be shown in the picker
 * based on whether the node has draggable/droppable/hotZone child nodes and the content type.
 * Returns an empty array if element states should be hidden entirely.
 */
export const useAllowedElementStates = ({
  hasDraggable,
  hasDroppable,
  hasHotZone,
  hasDial,
  hasSlider,
  contentNode,
}: Options): ElementStateOption[] => {
  const stateLabels = useElementStateLabels();

  return useMemo(() => {
    // WebObeject and Questions aren't really compatible with element states.
    if (contentNode?.typeId === "webObject" || contentNode?.role === "question") {
      return [];
    }

    const states: ElementStateOption[] = baseStates.map((state) => ({
      value: state,
      label: stateLabels[state].label,
      plainLabel: stateLabels[state].plainLabel,
    }));

    const hasDraggingContent = hasDial || hasSlider;

    if (hasDraggable) {
      states.push({ value: DIVIDER_OPTION_VALUE, label: "", group: "Draggable States" });
      states.push(
        ...draggableStates.map((state) => ({
          value: state,
          label: stateLabels[state].label,
          plainLabel: stateLabels[state].plainLabel,
        })),
      );
    } else if (hasDraggingContent) {
      const draggingState: ElementState = "dragging";
      states.push({ value: DIVIDER_OPTION_VALUE, label: "", group: "Dragging States" });
      states.push({
        value: draggingState,
        label: stateLabels[draggingState].label,
        plainLabel: stateLabels[draggingState].plainLabel,
      });
    }
    if (hasDroppable) {
      states.push({ value: DIVIDER_OPTION_VALUE, label: "", group: "Droppable States" });
      states.push(
        ...droppableStates.map((state) => ({
          value: state,
          label: stateLabels[state].label,
          plainLabel: stateLabels[state].plainLabel,
        })),
      );
    }
    if (hasHotZone) {
      states.push({ value: DIVIDER_OPTION_VALUE, label: "", group: "Hot Zone States" });
      states.push(
        ...hotZoneStates.map((state) => ({
          value: state,
          label: stateLabels[state].label,
          plainLabel: stateLabels[state].plainLabel,
        })),
      );
    }
    return states;
  }, [hasDraggable, hasDroppable, hasHotZone, hasDial, hasSlider, contentNode, stateLabels]);
};
